import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Ban, ShieldCheck, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1769650795569-e51a6e9580c3";

export default function BlockedList() {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/blocks").then((r) => setBlocked(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const unblock = async (b) => {
    setBusy(b.id);
    try {
      await api.post("/unblock", { target_id: b.id });
      setBlocked((prev) => prev.filter((x) => x.id !== b.id));
      toast.success(`Unblocked ${b.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-8" data-testid="blocked-page">
      <button onClick={() => navigate("/profile")} className="flex items-center gap-2 text-[#A0A0A0] hover:text-white mb-6 text-sm">
        <ArrowLeft size={16} /> Back to profile
      </button>
      <h2 className="font-heading text-3xl md:text-4xl flex items-center gap-3 mb-1">
        <Ban className="text-[#E63946]" /> Blocked members
      </h2>
      <p className="text-sm text-[#A0A0A0] mb-6">People you've blocked can't see you or message you. Unblock anytime.</p>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
      ) : blocked.length === 0 ? (
        <div data-testid="blocked-empty" className="pe-card rounded-2xl p-10 text-center">
          <ShieldCheck className="mx-auto text-[#D4AF37] mb-3" size={32} />
          <div className="text-white font-heading text-xl">You haven't blocked anyone</div>
          <p className="text-sm text-[#A0A0A0] mt-1">When you block someone, they'll show up here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blocked.map((b) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`blocked-row-${b.id}`}
              className="pe-card rounded-2xl p-4 flex items-center gap-4">
              <img src={b.photo || FALLBACK} alt={b.name} className="w-12 h-12 rounded-full object-cover border border-[#2A2A2A]" />
              <div className="flex-1 min-w-0 text-white font-medium truncate">{b.name}</div>
              <button data-testid={`unblock-${b.id}`} onClick={() => unblock(b)} disabled={busy === b.id}
                className="btn-outline-gold px-4 py-2 rounded-full text-sm flex items-center gap-2 disabled:opacity-60">
                {busy === b.id ? <Loader2 size={14} className="animate-spin" /> : null} Unblock
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
